/**
 * The one thing somebody must read before doing anything else with a record
 * (ADR-37).
 *
 * Matt, 2026-08-22: "every property, vendor and tenant should have an
 * 'Important Details' section in their data fields - this sits at the top of the
 * object's page when loading so its instantly called out to anyone that opens
 * the object if this field is being used - it should also be called out in a
 * color that forces the user to see it when they open the property, vendor,
 * utility or tenant - this is an important data point."
 *
 * So this is a shared component rather than a per-screen flourish: every entity
 * page that has the field renders THIS, in the same place, in the same colour.
 * A warning that looks different on the vendor page than on the property page is
 * a warning people learn to skim.
 *
 * Three rules it enforces:
 *
 *   * ABSENT when empty. A permanent empty band trains people to ignore the
 *     band, which is the one outcome that defeats the whole point.
 *   * FIRST in the page, above the record's own details. It is the reason the
 *     field exists: asbestos above the ceiling grid, the neighbour who calls the
 *     police, the gate that needs a key nobody has.
 *   * `role="alert"` so a screen reader announces it on arrival rather than
 *     leaving it to be discovered in reading order.
 *
 * Red, not amber. Amber is this design system's "warning" and would put it in
 * the same visual class as an expiring agreement; Matt asked for a colour that
 * forces the user to see it, and v1 uses red for exactly this band.
 */

import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

export function CriticalNotice({
  value,
  label = 'Important details',
}: {
  /** The record's critical-information text. Null or blank renders nothing. */
  readonly value: string | null | undefined;
  /** Overridable for entities that word it differently. */
  readonly label?: string | undefined;
}): ReactNode {
  if (value === null || value === undefined || value.trim() === '') return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-card border border-red-fg/40 bg-red-bg px-3 py-2.5"
    >
      <TriangleAlert size={18} aria-hidden className="mt-0.5 shrink-0 text-red-fg" />
      <div className="min-w-0">
        <span className="block text-chip font-semibold uppercase tracking-wide text-red-fg">
          {label}
        </span>
        {/* whitespace-pre-line: these are typed with line breaks that mean
            something — a list of hazards, a sequence of steps. */}
        <p className="whitespace-pre-line text-body font-medium text-red-fg">{value}</p>
      </div>
    </div>
  );
}
